import gsap from 'gsap';
import { Group, MeshPhongMaterial } from 'three';

export const PHONG_SETTINGS = {
  color: '#000000',
  shininess: 60,
};

import WebGLSketch from '../_app/cuchillo/3D/WebGLSketch';
import { GetBy } from '../_app/cuchillo/core/Element';
import { Metrics } from '../_app/cuchillo/core/Metrics';
import { Scroll } from '../_app/cuchillo/scroll/Scroll';
import Scene from './Scene';
import DebugPane from './DebugPane';
import Box from './Box';
import Tapon from './Tapon';

const TAPON_MODEL = '/assets/models/tapon-new.glb';
const TAPON_COLS = .5;
const TAPON_Z_FACTOR = .26;

// Rotació del box independent de l'scroll ABSOLUT (i per tant de l'aspect ratio / llargada
// de pàgina): es basa en la posició del box DINS el viewport. Al centre del viewport queda a
// la rotació base (frontal + una mica lateral); gira una mica en travessar-lo, però mai
// s'acumula fins a quedar d'esquena.
const BOX_ROT_BASE = Math.PI * -0.08;   // ~-29° — la cara del disseny queda frontal (offset ~-30° al model) + una mica lateral
const BOX_ROT_SCROLL = Math.PI * 0.3;   // gir total mentre travessa el viewport (±~27°)

export default class ArtScene {
  static group = new Group();

  static box = null;
  static tapon = null;

  static active = false;

  static init(container) {
    if (!WebGLSketch.renderer) WebGLSketch.init();

    WebGLSketch.scene.add(this.group);

    const boxEl = GetBy.class('__box', container)[0];
    const taponEl = GetBy.class('__tapon', container)[0];

    const categ = boxEl?.dataset.categ;
    Scene.initProductLights(categ);

    // BOX
    if (boxEl) {
      const material = new MeshPhongMaterial({
        color: categ === 'luminaris' ? 0xF1F1F1 : PHONG_SETTINGS.color,
        shininess: PHONG_SETTINGS.shininess,
      });

      this.box = new Box({
        model: boxEl.dataset.model,
        dom: boxEl,
        diffusion: boxEl.dataset.diffusion,
        specular: boxEl.dataset.specular,
        material,
        hasDrag: true,
        hasFlotation: true,
        hasFrontPlane: true,
        rotFactor: .2,
        cols: 22,
      });

      this.box.init().then(() => {
        this.box.active = true;

        Scene.setProductLayer(this.box, categ);

        this.group.add(this.box);
        this.box.resize();
      });
    }

    // TAPON
    if (taponEl) {
      const material = new MeshPhongMaterial({
        color: categ === 'luminaris' ? 0xF1F1F1 : 0x000000,
        shininess: PHONG_SETTINGS.shininess,
      });

      this.tapon = new Tapon({
        dom: taponEl,
        model: TAPON_MODEL,
        material,
        hasFlotation: true,
        cols: TAPON_COLS,
        // hasDrag: true
      });

      this.tapon.init().then(() => {
        this.tapon.active = true;
        Scene.setProductLayer(this.tapon, taponEl.dataset.type);

        this.group.add(this.tapon);
        this.tapon.resize();
      });
    }
  }

  static loop() {
    if (!this.active) return;

    // BOX
    if (this.box?.active) {
      const { x, y, width, height } =
        this.box.dom.getBoundingClientRect();

      if (width && height) {
        const pos = this.box.domPositionTo3D(x, y);

        this.box.pos.x = pos.x + width * .5;
        this.box.pos.y = pos.y - height * .5;
        this.box.pos.z = this.box.dataZ;

        // Gir relatiu a la posició del box dins el viewport (no a Scroll.y absolut).
        const boxCenterY = y + height * 0.5;                             // y de pantalla del centre
        const vp = (boxCenterY - Metrics.HEIGHT * 0.5) / Metrics.HEIGHT; // ~[-0.5,0.5], 0 = centrat
        this.box.rot.y = BOX_ROT_BASE + vp * BOX_ROT_SCROLL;

        this.box.update();
      }
    }

    // TAPON
    if (this.tapon?.active) {
      const { x, y, width, height } =
        this.tapon.dom.getBoundingClientRect();

      if (width && height) {
        const pos = this.tapon.domPositionTo3D(x, y);

        this.tapon.pos.x = pos.x + width * .5;
        this.tapon.pos.y = pos.y - height * .5;
        this.tapon.pos.z = Metrics.WIDTH * TAPON_Z_FACTOR;

        this.tapon.rot.y =
          Math.PI * .1 +
          Scroll.y * 0.002;

        this.tapon.update();
      }
    }
  }

  static animateIn() {
    this.active = true;
  }

  static hide() {
    this.active = false;
  }

  static resize() {
    this.box?.resize();
    this.tapon?.resize();
  }

  static dispose() {
    DebugPane.removeBox();

    Scene.disposeProductLights();

    if (this.box) {
      gsap.killTweensOf(this.box);

      this.group.remove(this.box);
      this.box.dispose();
      this.box = null;
    }

    if (this.tapon) {
      gsap.killTweensOf(this.tapon);

      this.group.remove(this.tapon);
      this.tapon.dispose();
      this.tapon = null;
    }

    WebGLSketch.scene.remove(this.group);
  }
}